import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, Crown, ArrowLeft } from 'lucide-react';
import { authService } from '@/services/auth-service';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import LanguageFlag from '@/components/LanguageFlag';
import { useAuthStore } from '@/stores/auth-store';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { getNativePremiumStoreName, getNativePlatform } from '@/utils/native-platform';

// ─── Translations ────────────────────────────────────────────────────────────

const copy = {
  sv: {
    title: 'Prisplaner',
    subtitle: 'Välj den plan som passar dig bäst.',
    free: {
      name: 'Gratis',
      description: 'För dig som vill uppnå myndigheternas grundläggande 7-dagarsrekommendation och komma igång med din hemberedskap.',
      price: '0 kr',
      period: '',
      cta: 'Kom igång gratis',
      note: '',
    },
    premium: {
      name: 'Premium',
      description: 'För dig som vill bygga upp en mer långsiktig och kapabel beredskap, samt samverka med andra i större utsträckning.',
      price: '69 kr',
      period: '/mån',
      cta: 'Skaffa Premium',
      badge: 'Rekommenderad',
    },
    sections: {
      home: 'Hemberedskapsläget',
      group: 'Gruppberedskapsläget',
    },
    freeFeatures: {
      home: [
        'Beredskapsplanering 7 dagar (Myndigheten för civilt försvars grundrekommendation)',
        'Checklistor för viktiga förberedelser',
        'Beredskapsförråd (med inventering och rotering)',
        'Vattenförråd',
        'Inköpslistor för mat och utrustning',
        'Kunskapsdatabas',
        'Familjeläge: delta i familjens beredskapsplanering som medlem',
      ],
      group: [
        'Gå med i upp till 2 beredskapsgrupper som medlem',
        'Delta i gruppdiskussioner, anslagstavlor och andra aktiviteter (med vissa begränsningar)',
      ],
    },
    premiumFeatures: {
      home: [
        'Allt i Gratis-planen',
        'Beredskapsplanering 7, 30 och 90 dagar+',
        'Årshjul för återkommande beredskapsplanering',
        'Receptbok för matplanering i vardag och kris',
        'Anteckningsbok för viktig beredskapsinfo och planering',
        'Familjeläge: skapa och bjud in medlemmar till familjens beredskapsplanering',
      ],
      group: [
        'Gå med i upp till 50 beredskapsgrupper som medlem eller ägare',
        'Skapa egna beredskapsgrupper och bjud in medlemmar',
        'Full tillgång till gruppdiskussioner, anslagstavlor och aktiviteter',
      ],
    },
    support: 'Personlig support via chatt',
    loggedIn: 'Gå till inställningar',
    back: 'Tillbaka',
  },
  en: {
    title: 'Pricing',
    subtitle: 'Choose the plan that works best for you.',
    free: {
      name: 'Free',
      description: 'For those who want to meet the Swedish authorities\' 7-day preparedness recommendation and get started with home preparedness.',
      price: 'SEK 0',
      period: '',
      cta: 'Get started for free',
      note: '',
    },
    premium: {
      name: 'Premium',
      description: 'For those who want to build a more long-term and capable preparedness, and collaborate with others to a greater extent.',
      price: 'SEK 69',
      period: '/month',
      cta: 'Get Premium',
      badge: 'Recommended',
    },
    sections: {
      home: 'Home Preparedness',
      group: 'Group Preparedness',
    },
    freeFeatures: {
      home: [
        '7-day preparedness planning (Swedish Civil Defence and Resilience Agency baseline recommendation)',
        'Checklists for essential preparations',
        'Supply storage (with inventory & rotation)',
        'Water storage',
        'Shopping lists for food and equipment',
        'Knowledge base',
        'Family mode: join your family\'s shared preparedness plan as a member',
      ],
      group: [
        'Join up to 2 preparedness groups as a member',
        'Participate in group discussions, bulletin boards and activities (with restrictions)',
      ],
    },
    premiumFeatures: {
      home: [
        'Everything in the Free plan',
        '7, 30 and 90+ day preparedness planning',
        'Year wheel for recurring preparedness tasks',
        'Recipe book for everyday and emergency meal planning',
        'Notebook for important preparedness information and planning',
        'Family mode: create and invite members to your family\'s shared preparedness plan',
      ],
      group: [
        'Join up to 50 preparedness groups as a member or owner',
        'Create your own preparedness groups and invite members',
        'Full access to group discussions, bulletin boards and activities',
      ],
    },
    support: 'Personal chat support',
    loggedIn: 'Go to settings',
    back: 'Back',
  },
} as const;

type Lang = 'sv' | 'en';

// ─── Feature row ─────────────────────────────────────────────────────────────

function FeatureItem({ text, included = true }: { text: string; included?: boolean }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      {included ? (
        <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5" />
      )}
      <span className={included ? 'text-foreground' : 'text-muted-foreground/50 line-through'}>
        {text}
      </span>
    </li>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PricingPage() {
  const [lang, setLang] = useState<Lang>('sv');
  const t = copy[lang];
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const nativePlatform = getNativePlatform();
  const nativeStoreName = getNativePremiumStoreName(nativePlatform);

  useEffect(() => {
    if (showOnboarding) lockScroll();
    else unlockScroll();
    return () => { unlockScroll(); };
  }, [showOnboarding]);

  useEffect(() => {
    document.title = `${t.title} - Beredskapshubben`;
  }, [t.title]);

  const handleFreeCta = () => {
    if (user) {
      navigate('/app');
      return;
    }
    setShowOnboarding(true);
  };

  const startLogin = async () => {
    setIsLoggingIn(true);
    try {
      const { authorizationUrl } = await authService.getOAuthUrl('microsoft', { prompt: 'login' });
      window.location.href = authorizationUrl;
    } catch {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <main className="relative py-8 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover opacity-40"
          style={{ backgroundImage: 'url(/background.png)', backgroundPosition: 'center 80%' }}
        />
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />

        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          {/* Language toggle */}
          <div className="flex justify-end mb-4 gap-2">
            <button
              onClick={() => setLang('sv')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                lang === 'sv'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <LanguageFlag lang="sv" />
              Svenska
            </button>
            <button
              onClick={() => setLang('en')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                lang === 'en'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <LanguageFlag lang="en" />
              English
            </button>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t.title}</h1>
            <p className="mt-2 text-muted-foreground">{t.subtitle}</p>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-2 gap-6 items-start">
            {/* ── Free ── */}
            <div className="bg-card border border-border rounded-xl p-6 space-y-6">
              {/* Plan header */}
              <div>
                <h2 className="text-xl font-semibold">{t.free.name}</h2>
                <p className="mt-1.5 text-sm text-muted-foreground">{t.free.description}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{t.free.price}</span>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t.sections.home}
                </p>
                <ul className="space-y-2">
                  {t.freeFeatures.home.map((f) => (
                    <FeatureItem key={f} text={f} />
                  ))}
                </ul>

                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">
                  {t.sections.group}
                </p>
                <ul className="space-y-2">
                  {t.freeFeatures.group.map((f) => (
                    <FeatureItem key={f} text={f} />
                  ))}
                </ul>
              </div>
            </div>

            {/* ── Premium ── */}
            <div className="bg-card border-2 border-orange-500 rounded-xl p-6 space-y-6 relative shadow-[0_0_24px_rgba(249,115,22,0.25)]">
              {/* Badge */}
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                {t.premium.badge}
              </span>

              {/* Plan header */}
              <div>
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-400" />
                  <h2 className="text-xl font-semibold">{t.premium.name}</h2>
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground">{t.premium.description}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{t.premium.price}</span>
                  <span className="text-muted-foreground">{t.premium.period}</span>
                </div>
              </div>

              {nativePlatform && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-muted-foreground">
                  <p>
                    {nativePlatform === 'android'
                      ? 'Premium köps säkert via Google Play i mobilappen.'
                      : 'Premium köps säkert via Apple App Store i mobilappen.'}
                  </p>
                  <p className="mt-2">
                    Öppna inställningarna i appen för att starta köp i {nativeStoreName}.
                  </p>
                </div>
              )}

              {/* Features */}
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t.sections.home}
                </p>
                <ul className="space-y-2">
                  {t.premiumFeatures.home.map((f) => (
                    <FeatureItem key={f} text={f} />
                  ))}
                </ul>

                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">
                  {t.sections.group}
                </p>
                <ul className="space-y-2">
                  {t.premiumFeatures.group.map((f) => (
                    <FeatureItem key={f} text={f} />
                  ))}
                </ul>

                {/* Support */}
                <ul className="space-y-2 pt-2">
                  <FeatureItem text={t.support} />
                </ul>
              </div>
            </div>
          </div>

          {/* CTA below cards */}
          <div className="mt-8 text-center">
            <button
              onClick={handleFreeCta}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg border-2 border-[#67724e] bg-card hover:bg-[#67724e]/10 font-semibold transition-colors text-foreground"
            >
              {user ? 'Gå till appen' : t.free.cta}
            </button>
          </div>

          {/* Back */}
          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{t.back}</span>
            </Link>
          </div>
        </div>
      </main>

      <PublicFooter />

      {/* Onboarding-modal */}
      {showOnboarding && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowOnboarding(false)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-border bg-background p-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowOnboarding(false)}
              className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Stäng"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-2xl font-bold text-foreground mb-8">Enkelt att komma igång</h2>

            <ol className="space-y-6">
              {[
                'Logga in eller skapa ett nytt konto.',
                'Vi skickar en engångskod till din e-postadress.',
                'Ange koden för att komma vidare.',
              ].map((text, i) => (
                <li key={i} className="flex items-center gap-4">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white font-bold text-lg"
                    style={{ backgroundColor: '#7a8a78' }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-foreground text-base">{text}</span>
                </li>
              ))}
            </ol>

            <button
              onClick={startLogin}
              disabled={isLoggingIn}
              className="mt-10 w-full inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-lg font-medium text-white transition-all hover:brightness-110 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#67724e' }}
            >
              {isLoggingIn ? 'Ansluter…' : 'Logga in / Skapa konto'}
            </button>

            <p className="mt-3 text-center text-xs text-muted-foreground/80">
              Inloggningssidan levereras i samarbete med Microsoft
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
